import { MenuButton, MenuItem, OverflowMenu, OverflowMenuItem } from '@carbon/react';
import type { ComponentType } from 'react';
import type { CdIconButtonProps } from '../../atoms/CdIconButton';
import type { IconComponent } from '../../types';
import { cx } from '../../utils';
import styles from './CdActionMenu.module.scss';

export interface CdActionMenuItem {
  id: string;
  label: string;
  icon?: IconComponent;
  onSelect?: () => void;
  danger?: boolean;
  /** Draws a divider above this item. */
  divider?: boolean;
}

interface BaseProps {
  items: CdActionMenuItem[];
  align?: 'start' | 'end';
  className?: string;
}

export interface IconActionMenuProps extends BaseProps {
  trigger: 'icon';
  /** Accessible name for the trigger, e.g. "Account". */
  label: string;
  /** Component rendered inside the trigger (icon or Avatar). */
  triggerIcon: ComponentType;
  /** Tooltip placement for the trigger; use an `-end` value when the trigger sits at the viewport edge. */
  tooltipAlign?: CdIconButtonProps['align'];
}

export interface TextActionMenuProps extends BaseProps {
  trigger: 'text';
  label: string;
  icon?: IconComponent;
  variant?: 'ghost' | 'tertiary' | 'primary';
}

export type CdActionMenuProps = IconActionMenuProps | TextActionMenuProps;

/**
 * Dropdown of actions. `trigger="icon"` for the avatar/overflow menus,
 * `trigger="text"` for labelled menus such as "Manage columns".
 */
export const CdActionMenu = (props: CdActionMenuProps) => {
  const { items, align = 'end', className } = props;

  if (props.trigger === 'text') {
    return (
      <MenuButton
        className={className}
        label={props.label}
        kind={props.variant ?? 'ghost'}
        size="sm"
        menuAlignment={align === 'end' ? 'bottom-end' : 'bottom-start'}
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            label={item.label}
            renderIcon={item.icon}
            kind={item.danger ? 'danger' : 'default'}
            onClick={item.onSelect}
          />
        ))}
      </MenuButton>
    );
  }

  return (
    <OverflowMenu
      className={cx(styles.iconTrigger, className)}
      aria-label={props.label}
      iconDescription={props.label}
      align={props.tooltipAlign}
      renderIcon={props.triggerIcon}
      flipped={align === 'end'}
      size="lg"
    >
      {items.map((item) => (
        <OverflowMenuItem
          key={item.id}
          hasDivider={item.divider}
          isDelete={item.danger}
          onClick={item.onSelect}
          itemText={
            <span className={styles.itemText}>
              {item.icon && <item.icon aria-hidden="true" />}
              {item.label}
            </span>
          }
        />
      ))}
    </OverflowMenu>
  );
};
